/*******************************************************************************
* Licensed Materials - Property of HCL 
* (c) Copyright HCL Technologies Ltd. 2020. All Rights Reserved. 

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/
/**
 * Component that displays 3 bubbles with task status
 * required params:
 *  - values: data array with 3 values
 *  Example : 
 *  [
        { data: 10, status: "ok", label: "No Risk" },
        { data: 6, status: "alert", label: "Potential Risk" },
        { data: 8, status: "ko", label: "High Risk" },
    ]
 */

import React from "react";
import PropTypes from "prop-types";
import Text from "../GUI/Text";

function BallsChart({ data, textColor, width, height, ...otherAttributes }) {
    if (data.length !== 3) return null;
    const padding = { x: width * 0.1, y: height * 0.1 };

    const createLabels = () => {
        const elemLen = width / 3;
        return data.map((elem, index) => {
            return (
                <a-entity
                    key={index}
                    position={`${index * elemLen + elemLen * 0.08} 0 0`}
                >
                    <a-box
                        position={`0 0 ${elemLen * 0.01}`}
                        color={elem.color}
                        width={elemLen * 0.15}
                        height={elemLen * 0.15}
                        depth={elemLen * 0.025}
                    />
                    <Text
                        text={elem.label}
                        position={`${elemLen * 0.1} 0 ${elemLen * 0.0125}`}
                        align="left"
                        width={elemLen * 0.75}
                        sizeDecimal={12.5 / elemLen}
                    />
                </a-entity>
            );
        });
    };

    // Takes labels and return an array of Text ready to be renderized by React
    const createSpheres = () => {
        const referenceWidth = width;
        const maxValue = data.reduce((p, c) => (c.value > p ? c.value : p), 0);
        const sumValue = data.reduce(
            (p, c) =>
                p +
                (c.value > maxValue * 0.25 ? c.value : maxValue * 0.25 || 1),
            0
        );

        let currentPosition = 0;

        return data.map((elem, index) => {
            let radius =
                (((referenceWidth - 4 * padding.x) / sumValue) *
                    (elem.value > maxValue * 0.25
                        ? elem.value
                        : maxValue * 0.25 || 1)) /
                2;

            if (radius > height * 0.15) radius = height * 0.15;

            const item = (
                <a-entity
                    key={index}
                    position={`${
                        currentPosition +
                        (((referenceWidth - 4 * padding.x) / sumValue) *
                            (elem.value > maxValue * 0.25
                                ? elem.value
                                : maxValue * 0.25 || 1)) /
                            2
                    } ${
                        index === 1
                            ? -(
                                  ((referenceWidth - 4 * padding.x) /
                                      sumValue) *
                                  (maxValue || 1)
                              ) / 2.5
                            : 0
                    } 0`}
                >
                    <a-cylinder
                        radius={radius}
                        height={radius * 0.1}
                        color={elem.color}
                        rotation="90 0 0"
                    />

                    <Text
                        align="center"
                        position={!elem.value ? 
                            `-0.002 0.005 ${radius * 0.1}` : `${(elem.value.toString().length * -0.002) -0.001} 0.005 ${radius * 0.1}`
                        }
                        text={elem.value.toString()}
                        width={radius * 2}
                        sizeDecimal={19 / referenceWidth}
                        color="black"
                    />
                </a-entity>
            );

            currentPosition +=
                padding.x +
                ((referenceWidth - 4 * padding.x) / sumValue) *
                    (elem.value > maxValue * 0.25
                        ? elem.value
                        : maxValue * 0.25 || 1);
            return item;
        });
    };

    return (
        <a-entity class="BallsChart" {...otherAttributes}>
            <a-entity
                position={`${-width / 2 + padding.x} ${
                    height / 2 - padding.y - height * 0.15
                } 0`}
            >
                {createSpheres()}
            </a-entity>
            <a-entity
                position={`${-width / 2 + padding.x / 2} ${
                    -height / 2 + padding.y + height * 0.1
                } 0`}
            >
                {createLabels()}
            </a-entity>
        </a-entity>
    );
}

// requiring dataPoints
BallsChart.propTypes = {
    data: PropTypes.array.isRequired
};

export default BallsChart;
